/**
 * The editor's icon set: small inline SVGs drawn on a 16×16 grid with
 * `currentColor` strokes, so every icon inherits its color from theme CSS.
 * No icon library (CLAUDE.md rule 10, A-32).
 */
import type { SVGProps } from "react";

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "children"> {
  readonly size?: number;
}

/** Wraps path data in the shared icon frame. */
function icon(paths: readonly string[], displayName: string) {
  function Icon({ size = 16, ...rest }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        {...rest}
      >
        {paths.map((d) => (
          <path key={d} d={d} />
        ))}
      </svg>
    );
  }
  Icon.displayName = displayName;
  return Icon;
}

export type IconComponent = ReturnType<typeof icon>;

// ─── Actions ─────────────────────────────────────────────────────────────
export const MenuIcon = icon(["M2.5 4h11", "M2.5 8h11", "M2.5 12h11"], "MenuIcon");
export const DownloadIcon = icon(
  ["M8 2v8", "M4.5 7L8 10.5 11.5 7", "M2.5 13.5h11"],
  "DownloadIcon",
);
export const UndoIcon = icon(["M4 6.5h6a3 3 0 010 6H6", "M6.5 4L4 6.5 6.5 9"], "UndoIcon");
export const RedoIcon = icon(["M12 6.5H6a3 3 0 000 6h4", "M9.5 4L12 6.5 9.5 9"], "RedoIcon");
export const PlayIcon = icon(["M5 3.5v9l7-4.5z"], "PlayIcon");
export const PauseIcon = icon(["M5.5 3.5v9", "M10.5 3.5v9"], "PauseIcon");
export const StopIcon = icon(["M4 4h8v8H4z"], "StopIcon");
export const ChevronDownIcon = icon(["M4 6l4 4 4-4"], "ChevronDownIcon");
export const ChevronRightIcon = icon(["M6 4l4 4-4 4"], "ChevronRightIcon");
export const SearchIcon = icon(
  ["M7 12a5 5 0 100-10 5 5 0 000 10z", "M10.5 10.5L14 14"],
  "SearchIcon",
);
export const EyeIcon = icon(
  [
    "M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z",
    "M8 10a2 2 0 100-4 2 2 0 000 4z",
  ],
  "EyeIcon",
);
export const EyeOffIcon = icon(
  [
    "M2 2l12 12",
    "M6.5 3.8A6.8 6.8 0 018 3.5c4 0 6.5 4.5 6.5 4.5a11 11 0 01-1.7 2.2",
    "M4.2 5.2A10.6 10.6 0 001.5 8S4 12.5 8 12.5a6.3 6.3 0 002.8-.6",
  ],
  "EyeOffIcon",
);

// ─── Transform tools ─────────────────────────────────────────────────────
export const SelectIcon = icon(["M3.5 2.5l9 4-4 1.5-1.5 4z"], "SelectIcon");
export const MoveIcon = icon(
  [
    "M8 1.5v13",
    "M1.5 8h13",
    "M6 3.5l2-2 2 2",
    "M6 12.5l2 2 2-2",
    "M3.5 6l-2 2 2 2",
    "M12.5 6l2 2-2 2",
  ],
  "MoveIcon",
);
export const RotateIcon = icon(["M13 8a5 5 0 11-1.5-3.5", "M13 2.5v2.5h-2.5"], "RotateIcon");
export const ScaleIcon = icon(["M2.5 13.5h5v-5h-5z", "M7.5 8.5l6-6", "M9.5 2.5h4v4"], "ScaleIcon");

// ─── Links and chrome ────────────────────────────────────────────────────
export const CodeIcon = icon(["M5.5 4.5L2 8l3.5 3.5", "M10.5 4.5L14 8l-3.5 3.5"], "CodeIcon");
export const LinkIcon = icon(
  ["M6.5 9.5l3-3", "M7.5 4.5l1-1a2.8 2.8 0 014 4l-1 1", "M8.5 11.5l-1 1a2.8 2.8 0 01-4-4l1-1"],
  "LinkIcon",
);
export const TerminalIcon = icon(["M2.5 4l4 4-4 4", "M8 12.5h5.5"], "TerminalIcon");
/** Bars, for the Statistics panel. */
export const StatsIcon = icon(["M3 13.5V8", "M8 13.5V3", "M13 13.5V6"], "StatsIcon");
export const FolderIcon = icon(["M1.5 4.5v8h13v-7H7.5L6 4H1.5z"], "FolderIcon");
export const PlusIcon = icon(["M8 3v10", "M3 8h10"], "PlusIcon");
export const FileIcon = icon(["M3.5 1.5h6l3 3v10h-9z", "M9.5 1.5v3h3"], "FileIcon");
export const SunIcon = icon(
  [
    "M8 11a3 3 0 100-6 3 3 0 000 6z",
    "M8 1v1.5",
    "M8 13.5V15",
    "M1 8h1.5",
    "M13.5 8H15",
    "M3 3l1 1",
    "M12 12l1 1",
    "M3 13l1-1",
    "M12 4l1-1",
  ],
  "SunIcon",
);
export const MoonIcon = icon(["M13 9.5A5.5 5.5 0 016.5 3a5.5 5.5 0 106.5 6.5z"], "MoonIcon");

// ─── Viewport toolbar ────────────────────────────────────────────────────
export const GridIcon = icon(["M5.5 2v12", "M10.5 2v12", "M2 5.5h12", "M2 10.5h12"], "GridIcon");
export const MagnetIcon = icon(
  ["M3.5 2.5v5a4.5 4.5 0 009 0v-5", "M3.5 5.5h2.5", "M10 5.5h2.5", "M6 2.5v5a2 2 0 004 0v-5"],
  "MagnetIcon",
);

// ─── Entity types (Outliner) ─────────────────────────────────────────────
export const LevelIcon = icon(
  ["M8 1.5l6 3.25v6.5L8 14.5l-6-3.25v-6.5z", "M2 4.75l6 3.25 6-3.25", "M8 8v6.5"],
  "LevelIcon",
);
export const ZoneIcon = icon(["M3 3h10v10H3z"], "ZoneIcon");
export const MeshIcon = icon(["M8 1.5l5.5 3.25v6.5L8 14.5l-5.5-3.25v-6.5z"], "MeshIcon");
export const FxIcon = icon(
  [
    "M8 1.5v3",
    "M8 11.5v3",
    "M1.5 8h3",
    "M11.5 8h3",
    "M3.5 3.5l2 2",
    "M10.5 10.5l2 2",
    "M3.5 12.5l2-2",
    "M10.5 5.5l2-2",
  ],
  "FxIcon",
);
export const TextIcon = icon(["M3 3.5h10", "M8 3.5v9", "M6 12.5h4"], "TextIcon");
export const ImageIcon = icon(
  ["M2 3h12v10H2z", "M2 11l3.5-3.5 2.5 2.5 2-2 4 4", "M10.5 5.5a1 1 0 110 2 1 1 0 010-2z"],
  "ImageIcon",
);
export const PanelIcon = icon(
  ["M2 3h12v10H2z", "M4.5 6h7", "M4.5 8.5h5", "M4.5 11h6"],
  "PanelIcon",
);
export const LightIcon = icon(
  ["M8 1.5a4 4 0 00-2.5 7.1V11h5V8.6A4 4 0 008 1.5z", "M6 13.5h4"],
  "LightIcon",
);
export const CameraIcon = icon(["M1.5 5h9v6h-9z", "M10.5 7l4-2v6l-4-2"], "CameraIcon");
/** A camera with speed lines behind it (the viewport's camera speed). */
export const CameraSpeedIcon = icon(
  ["M5 5h7v6H5z", "M12 7l3-1.5v5L12 9", "M1 6.5h2.5", "M1.5 9.5h2.5"],
  "CameraSpeedIcon",
);
export const PostIcon = icon(["M2 2h12v12H2z", "M2 10l4-4 3 3 2-2 3 3"], "PostIcon");
/** Three drifting lines: the level's fog. */
export const FogIcon = icon(["M2 5h9", "M4 8h10", "M2 11h8"], "FogIcon");
/** A cloud: the level's sky panorama. */
export const SkyIcon = icon(
  ["M4.5 12.5h7a2.5 2.5 0 00.3-5A3.5 3.5 0 005 6.6a3 3 0 00-.5 5.9z"],
  "SkyIcon",
);
/** A plain entity with no typed component (an empty actor / GameObject). */
export const EntityIcon = icon(["M8 5.5a2.5 2.5 0 110 5 2.5 2.5 0 010-5z"], "EntityIcon");

/** Icon names used by component schemas' `display.icon` (plus "folder" and "entity"). */
const ENTITY_ICONS: Readonly<Record<string, IconComponent>> = {
  level: LevelIcon,
  zone: ZoneIcon,
  mesh: MeshIcon,
  fx: FxIcon,
  panel: PanelIcon,
  text: TextIcon,
  image: ImageIcon,
  light: LightIcon,
  camera: CameraIcon,
  post: PostIcon,
  sky: SkyIcon,
  fog: FogIcon,
  link: LinkIcon,
  folder: FolderIcon,
  entity: EntityIcon,
};

/** The icon for a schema icon name (`display.icon`); unknown names get the plain entity icon. */
export function EntityTypeIcon({ name, ...props }: IconProps & { readonly name: string }) {
  const Icon = ENTITY_ICONS[name] ?? EntityIcon;
  return <Icon {...props} />;
}
